import { useEffect, useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { codiceInvito, leggiCodiceInvito } from '@/lib/gamehost/rest';
import { useAzioneTavolo, type GuastoAzione } from '@/lib/gamehost/useAzioneTavolo';
import { LIVELLI_ROBOT, eRobotNonDisponibili } from '@/lib/settemezzo/robot';
import type { LivelloRobot } from '@/lib/settemezzo/robot';
import { settemezzoApi } from '@/services/limuloSettemezzo';

// COPIA DICHIARATA da TMA#746 (briscola-orcb-8-tma) @ e823c650454b6adf11046bffc675774d6429937d: src/components/briscola/GiocaBriscola.tsx, righe 13-14 (i tipi `Azione` e `Guasto`), nella forma di
// src/components/ginrummy/GiocaGinRummy.tsx @ 8a3aab5bf3a9cf073711ceeb9d50f7837089efaa (TMA#763): cosa e diverso dal gin: i nomi (dominio `settemezzo`, catalogo `settemezzo`), niente altro.
type Azione = 'gioca' | 'invita' | 'entra' | 'robot';
type Guasto = GuastoAzione | 'codiceNonValido' | 'robotNonDisponibili';

/** La frase di un guasto: una chiave di una lista chiusa sotto `hub.gioca.guasto.`. Il testo del server non si mostra MAI. */
function chiaveGuasto(azione: Azione, g: Guasto): string {
  switch (g) {
    case 'non-disponibile':
      return 'nonDisponibile';
    case 'conflitto':
      return azione === 'entra' ? 'conflittoEntra' : 'conflittoCrea';
    case 'nonTrovato':
      return azione === 'entra' ? 'nonTrovato' : 'errore';
    default:
      return g;
  }
}

/**
 * «GIOCA», «CONTRO IL ROBOT», «INVITA UN AMICO» ed «ENTRA CON UN CODICE» nella Hub del sette e mezzo (sul modello di `GiocaGinRummy`, a sua volta di `GiocaScopa`, sul trasporto comune): le SCRITTURE, ognuna con la sua chiave
 * di idempotenza (`useAzioneTavolo`: il doppio tocco non crea due tavoli). Il sette e mezzo parte uno contro uno (r. 4 del regolamento: banco a ogni mano, 10 mani), quindi non c'e la scelta dei posti: sono sempre 2.
 *  · «Gioca»: un tavolo casual (`settemezzo.table.create`), apre `/settemezzo/tavolo/:id`. Chi completa il tavolo (una persona, o un robot) lo decide il game host, non la TMA.
 *  · «Contro il robot» (L0 Principiante, L1 Intermedio, L2 Esperto; la forma e COPIA DICHIARATA di `GiocaBriscola`, sotto): un tavolo casual col campo `robot: { livello }`. Chi crea siede a un
 *    posto estratto dal core e l'altro posto e un robot del pool, col badge «bot». Senza account nel pool il tavolo non nasce (503 `no_robot_available`, riconosciuto dallo status E dal codice):
 *    si dice in chiaro; un altro 503 resta un guasto di servizio. ⚠️ Il campo `robot` arriva al game host solo dopo il mirror di app-qsettemezzo su Limulo staging (INNESTO §9 di QSetteMezzo).
 *  · «Invita un amico»: un tavolo privato; mostra il CODICE da condividere e NON entra da solo (l'invito si vede una volta sola).
 *  · «Entra con un codice»: `settemezzo.table.join` col `tableId` e l'invito letti dal codice. Un codice che non ha la forma non parte nemmeno.
 * Il codice e una credenziale: sta SOLO nello stato del componente, mai in storage, in log o nell'URL. Nessun tavolo «in classifica»: ranked e tornei NON entrano (il sette e mezzo nasce in amichevole contro il robot e su invito).
 *
 * 🔴 Ogni azione ha una chiave di idempotenza PER CORPO: «Gioca», «Invita» e ognuno dei tre livelli sono corpi diversi, e una chiave rimasta da un guasto incerto di uno riusata per un altro
 * prende 409 `idempotency_key_reused` dal core. Tornando all'azione di prima si ritrova la SUA chiave: ripetere la stessa azione dopo un esito incerto ritrova lo stesso tavolo.
 * Lasciato fuori rispetto alla briscola: la scelta dei posti (il sette e mezzo si apre solo 1v1) e il suo 2v2.
 */
export function GiocaSettemezzo() {
  const { t } = useTranslation('settemezzo');
  const navigate = useNavigate();
  const titoloId = useId();
  const [crea] = settemezzoApi.useCreateSettemezzoTableMutation();
  const [entra] = settemezzoApi.useJoinSettemezzoTableMutation();
  const aGioca = useAzioneTavolo<{ tableId: string }>();
  const aInvita = useAzioneTavolo<{ tableId: string; codice: string | null }>();
  const aEntra = useAzioneTavolo<{ tableId: string }>();
// COPIA DICHIARATA da TMA#746 (briscola-orcb-8-tma) @ e823c650454b6adf11046bffc675774d6429937d: src/components/briscola/GiocaBriscola.tsx, righe 56-59, 68-71 (un'azione per livello, `livelloUltimo`, `senzaRobot`, `occupato`; senza la dimensione dei posti).
  const aRobot = { L0: useAzioneTavolo<{ tableId: string }>(), L1: useAzioneTavolo<{ tableId: string }>(), L2: useAzioneTavolo<{ tableId: string }>() };
  const [invitoPronto, setInvitoPronto] = useState<{ tableId: string; codice: string | null } | null>(null);
  const [testo, setTesto] = useState('');
  const [codiceStorto, setCodiceStorto] = useState(false);
  const [ultima, setUltima] = useState<Azione | null>(null);
  const [livelloUltimo, setLivelloUltimo] = useState<LivelloRobot>('L0');
  const [senzaRobot, setSenzaRobot] = useState(false);
  const occupato = [aGioca, aInvita, aEntra, ...LIVELLI_ROBOT.map((l) => aRobot[l])].some((a) => a.stato.invio);

  const vaiAlTavolo = (tableId: string) => navigate(`/settemezzo/tavolo/${tableId}`);

  const gioca = async () => {
    setUltima('gioca');
    setInvitoPronto(null);
    const r = await aGioca.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'casual', idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId };
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

// COPIA DICHIARATA da TMA#746 (briscola-orcb-8-tma) @ e823c650454b6adf11046bffc675774d6429937d: src/components/briscola/GiocaBriscola.tsx, righe 85-100 (`controIRobot`).
  const controIlRobot = async (livello: LivelloRobot) => {
    setUltima('robot');
    setLivelloUltimo(livello);
    setSenzaRobot(false);
    setInvitoPronto(null);
    const r = await aRobot[livello].esegui(async (idempotencyKey) => {
      try {
        const tavolo = await crea({ tipo: 'casual', idempotencyKey, livello }).unwrap();
        return tavolo === null ? null : { tableId: tavolo.tableId };
      } catch (e) {
        if (eRobotNonDisponibili(e)) setSenzaRobot(true);
        throw e;
      }
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

  const invita = async () => {
    setUltima('invita');
    setInvitoPronto(null);
    const r = await aInvita.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'privato', idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId, codice: tavolo.invito === null ? null : codiceInvito(tavolo.tableId, tavolo.invito) };
    });
    if (r !== null) setInvitoPronto(r);
  };

  const entraCol = async (e: FormEvent) => {
    e.preventDefault();
    setUltima('entra');
    setInvitoPronto(null);
    const letto = leggiCodiceInvito(testo);
    setCodiceStorto(letto === null);
    if (letto === null) return; // niente richiesta per un codice che non ha la forma
    const r = await aEntra.esegui(async (idempotencyKey) => {
      const ingresso = await entra({ tableId: letto.tableId, invito: letto.invito, idempotencyKey }).unwrap();
      return ingresso === null ? null : { tableId: ingresso.tableId };
    });
    if (r !== null) {
      setTesto('');
      vaiAlTavolo(r.tableId);
    }
  };

// COPIA DICHIARATA da TMA#746 (briscola-orcb-8-tma) @ e823c650454b6adf11046bffc675774d6429937d: src/components/briscola/GiocaBriscola.tsx, righe 129-143 (il guasto che si mostra; senza `posti`).
  const guastoRobot = aRobot[livelloUltimo].stato.guasto;
  const guasto: Guasto | null =
    ultima === 'entra' && codiceStorto
      ? 'codiceNonValido'
      : ultima === 'gioca'
        ? aGioca.stato.guasto
        : ultima === 'invita'
          ? aInvita.stato.guasto
          : ultima === 'robot'
            ? senzaRobot && guastoRobot === 'server'
              ? 'robotNonDisponibili'
              : guastoRobot
            : ultima === 'entra'
              ? aEntra.stato.guasto
              : null;

  return (
    <section data-testid="settemezzo-gioca" aria-labelledby={titoloId} className="flex shrink-0 flex-col gap-3">
      <h2 id={titoloId} className="text-xs font-bold uppercase tracking-wide text-fg-muted">
        {t('hub.gioca.titolo')}
      </h2>
      <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.nota')}</p>
      <Button variant="primary" fullWidth data-azione="gioca" disabled={occupato} onClick={() => void gioca()}>
        {aGioca.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.gioca')}
      </Button>
      {/* COPIA DICHIARATA da TMA#746 (briscola-orcb-8-tma) @ e823c650454b6adf11046bffc675774d6429937d: src/components/briscola/GiocaBriscola.tsx, righe 162-172 (il blocco dei tre livelli; la nota e la sola del 1v1). */}
      <div data-robot className="flex flex-col gap-2">
        <p className="text-sm font-semibold text-fg">{t('hub.gioca.robot.titolo')}</p>
        <div className="grid grid-cols-3 gap-2">
          {LIVELLI_ROBOT.map((livello) => (
            <Button key={livello} variant="secondary" data-azione="robot" data-livello={livello} disabled={occupato} onClick={() => void controIlRobot(livello)}>
              {aRobot[livello].stato.invio ? t('hub.gioca.invio') : t(`livello.${livello}`)}
            </Button>
          ))}
        </div>
        <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.robot.nota')}</p>
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="secondary" fullWidth data-azione="invita" disabled={occupato} onClick={() => void invita()}>
          {aInvita.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.invita')}
        </Button>
        <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.invitaNota')}</p>
      </div>
      {invitoPronto !== null && <InvitoPronto pronto={invitoPronto} onVai={() => vaiAlTavolo(invitoPronto.tableId)} />}
      <form data-azione="entra" onSubmit={(e) => void entraCol(e)} className="flex flex-col gap-2">
        <label htmlFor={`${titoloId}-codice`} className="text-sm font-semibold text-fg">
          {t('hub.gioca.entra.titolo')}
        </label>
        <input
          id={`${titoloId}-codice`}
          type="text"
          value={testo}
          onChange={(e) => {
            setTesto(e.target.value);
            setCodiceStorto(false);
          }}
          placeholder={t('hub.gioca.entra.segnaposto')}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          disabled={occupato}
          className="min-h-11 w-full min-w-0 rounded-xl border border-border bg-bg-secondary px-3 text-sm text-fg placeholder:text-fg-muted"
        />
        <Button type="submit" variant="secondary" fullWidth disabled={occupato || testo.trim() === ''}>
          {aEntra.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.entra.azione')}
        </Button>
      </form>
      {guasto !== null && (
        <p role="status" data-gioca-guasto={guasto} className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-danger">
          {t(`hub.gioca.guasto.${chiaveGuasto(ultima ?? 'gioca', guasto)}`)}
        </p>
      )}
    </section>
  );
}

/** Il codice da condividere, copiabile. «Copiato» si dice SOLO se la copia e riuscita; a copia fallita il codice si seleziona con un tocco lungo. */
function InvitoPronto({ pronto, onVai }: { pronto: { tableId: string; codice: string | null }; onVai: () => void }) {
  const { t } = useTranslation('settemezzo');
  const [esito, setEsito] = useState<'ok' | 'ko' | null>(null);
  useEffect(() => setEsito(null), [pronto.codice]);
  const copia = async () => {
    if (pronto.codice === null) return;
    try {
      await navigator.clipboard.writeText(pronto.codice);
      setEsito('ok');
    } catch {
      setEsito('ko');
    }
  };
  return (
    <div data-settemezzo-invito-pronto className="card-edge flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
      {pronto.codice === null ? (
        <p data-settemezzo-invito-assente className="text-sm text-fg-muted">
          {t('hub.gioca.codice.assente')}
        </p>
      ) : (
        <>
          <p className="text-sm font-semibold text-fg">{t('hub.gioca.codice.titolo')}</p>
          <code data-settemezzo-invito-codice className="select-all break-all rounded bg-bg-tertiary px-2 py-2 font-mono text-xs text-fg">
            {pronto.codice}
          </code>
          <p className="text-xs text-fg-muted">{t('hub.gioca.codice.avviso')}</p>
          <Button variant="secondary" fullWidth data-azione="copia" onClick={() => void copia()}>
            {esito === 'ok' ? t('hub.gioca.codice.copiato') : t('hub.gioca.codice.copia')}
          </Button>
          <p role="status" aria-live="polite" className={esito === 'ko' ? 'text-xs text-fg-muted' : 'sr-only'}>
            {esito === 'ko' ? t('hub.gioca.codice.copiaFallita') : esito === 'ok' ? t('hub.gioca.codice.copiato') : ''}
          </p>
        </>
      )}
      <Button variant="primary" fullWidth data-azione="vai" onClick={onVai}>
        {t('hub.gioca.codice.vai')}
      </Button>
    </div>
  );
}
